import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Heart, Package, MapPin, MessageCircle, DollarSign, 
  Shield, Send, CreditCard, Loader2, Edit, ChevronLeft, ChevronRight, Truck, ExternalLink,
  Share2, Link2, Check, Rocket, Sparkles, Flag, AlertTriangle, ShoppingCart, Tag
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { CountdownTimer } from "@/components/CountdownTimer";
import { formatPrice, PAYPAL_CLIENT_ID } from "@/utils/constants";
import { BoostListingModal, SponsoredBadge, SponsoredStatsCard } from "@/components/SponsoredListings";
import { PurchaseBreakdown } from "@/components/PurchaseBreakdown";
import { TrustBadge, SellerTrustCard } from "@/components/KYCVerification";
import { ListingVerificationCard, VerificationBadge } from "@/components/ListingVerification";
import { RequestInspectionModal, SellerVerificationBadge } from "@/components/InspectorMarketplace";
import api from "@/utils/api";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { SameDayDeliveryOption } from "@/components/SameDayDelivery";

const NO_IMAGE_PLACEHOLDER = "https://placehold.co/400x400/e2e8f0/64748b?text=No+Image";
const BIDZUU_LOGO = "https://customer-assets.emergentagent.com/job_auction-plaza-1/artifacts/f3twp3sf_2.png";

const ItemDetailPage = ({ user, userCurrency }) => {
  const location = useLocation();
  const listingId = location.pathname.split('/')[2];
  const queryParams = new URLSearchParams(location.search);
  const acceptedOfferId = queryParams.get('offer');
  const [acceptedOffer, setAcceptedOffer] = useState(null);
  const [listing, setListing] = useState(null);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bidAmount, setBidAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [inWatchlist, setInWatchlist] = useState(false);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [showOfferDialog, setShowOfferDialog] = useState(false);
  const [showBuyNowDialog, setShowBuyNowDialog] = useState(false);
  const [showBoostModal, setShowBoostModal] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [offerAmount, setOfferAmount] = useState("");
  const [offerMessage, setOfferMessage] = useState("");
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [linkCopied, setLinkCopied] = useState(false);
  const [shippingEstimates, setShippingEstimates] = useState(null);
  const [buyerZip, setBuyerZip] = useState("");
  const [loadingShipping, setLoadingShipping] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportDetails, setReportDetails] = useState("");
  const [submittingReport, setSubmittingReport] = useState(false);
  const [showSafetyTips, setShowSafetyTips] = useState(false);
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [availableInspectors, setAvailableInspectors] = useState([]);
  const [loadingInspectors, setLoadingInspectors] = useState(false);
  // Cart & Quantity states
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  // Variant selection states
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const navigate = useNavigate();

  // Resolve the currently selected variant (if listing has variants)
  const getActiveVariant = () => {
    if (!listing?.variants || listing.variants.length === 0) return null;
    const colors = listing.variants.map(v => v.color).filter(Boolean);
    const hasColors = colors.length > 0;
    const sizes = listing.variants.map(v => v.size).filter(Boolean);
    const hasSizes = sizes.length > 0;
    return listing.variants.find(v =>
      (!hasColors || v.color === selectedColor) &&
      (!hasSizes || v.size === selectedSize)
    ) || null;
  };

  const requiresVariantSelection = () => {
    if (!listing?.variants || listing.variants.length === 0) return false;
    const v = getActiveVariant();
    return !v;
  };

  // Total stock across the whole listing
  // - For variant listings: sum of all variant quantities
  // - For non-variant listings: listing.quantity (default 1 if undefined)
  const getTotalStock = () => {
    if (!listing) return 0;
    if (listing.variants && listing.variants.length > 0) {
      return listing.variants.reduce((sum, v) => sum + (parseInt(v.quantity, 10) || 0), 0);
    }
    if (listing.quantity === undefined || listing.quantity === null) return 1;
    return parseInt(listing.quantity, 10) || 0;
  };

  const isOutOfStock = () => {
    if (!listing || listing.status !== 'active') return false;
    return getTotalStock() === 0;
  };

  // Report reasons
  const REPORT_REASONS = [
    { value: "fake_listing", label: "Fake or fraudulent listing" },
    { value: "counterfeit", label: "Counterfeit or replica item" },
    { value: "stolen_photos", label: "Stolen photos from another source" },
    { value: "payment_scam", label: "Seller requesting off-platform payment" },
    { value: "misleading_description", label: "Misleading description" },
    { value: "prohibited_item", label: "Prohibited or illegal item" },
    { value: "wrong_category", label: "Wrong category" },
    { value: "other", label: "Other" }
  ];

  // Handle report submission
  const handleReportListing = async () => {
    if (!reportReason) {
      toast.error("Please select a reason for reporting");
      return;
    }
    
    setSubmittingReport(true);
    try {
      await api.post(`/listings/${listingId}/report`, {
        reason: reportReason,
        details: reportDetails
      });
      toast.success("Report submitted. Thank you for helping keep BidZuu safe.");
      setShowReportDialog(false);
      setReportReason("");
      setReportDetails("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit report");
    } finally {
      setSubmittingReport(false);
    }
  };

  // Share functionality
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = listing?.title || 'Check out this item on BidZuu';
  const shareText = listing ? `${listing.title} - $${listing.price?.toFixed(2)} on BidZuu` : '';

  const handleShare = async (platform) => {
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedText = encodeURIComponent(shareText);
    const encodedTitle = encodeURIComponent(shareTitle);

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, '_blank', 'width=600,height=400');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`, '_blank', 'width=600,height=400');
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodedText}%20${encodedUrl}`, '_blank');
        break;
      case 'copy':
        try {
          await navigator.clipboard.writeText(shareUrl);
          setLinkCopied(true);
          toast.success("Link copied to clipboard!");
          setTimeout(() => setLinkCopied(false), 2000);
        } catch (err) {
          toast.error("Failed to copy link");
        }
        break;
      case 'native':
        if (navigator.share) {
          try {
            await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
          } catch (err) {
            if (err.name !== 'AbortError') {
              toast.error("Failed to share");
            }
          }
        }
        break;
      default:
        break;
    }
  };

  // Fetch shipping estimates
  const fetchShippingEstimate = async (zip) => {
    if (!listing || !zip) return;
    try {
      setLoadingShipping(true);
      const response = await api.get(`/shipping/estimate`, {
        params: { listing_id: listingId, buyer_zip: zip }
      });
      setShippingEstimates(response.data);
    } catch (error) {
      console.error('Failed to get shipping estimate:', error);
    } finally {
      setLoadingShipping(false);
    }
  };

  useEffect(() => {
    const fetchListing = async () => {
      try {
        const [listingRes, watchlistRes] = await Promise.all([
          api.get(`/listings/${listingId}`),
          api.get(`/watchlist/check/${listingId}`).catch(() => ({ data: { in_watchlist: false } }))
        ]);
        setListing(listingRes.data.listing);
        setBids(listingRes.data.bids);
        setInWatchlist(watchlistRes.data.in_watchlist);
        
        // Fetch available inspectors for this listing
        fetchAvailableInspectors(listingId);
      } catch (error) {
        toast.error("Failed to load item");
        navigate('/browse');
      } finally {
        setLoading(false);
      }
    };
    fetchListing();
  }, [listingId, navigate]);

  // If we came in via ?offer=<message_id>, load the accepted offer and auto-open Buy Now dialog
  useEffect(() => {
    if (!acceptedOfferId || !user?.user_id) return;
    let cancelled = false;
    const loadOffer = async () => {
      try {
        const res = await api.get(`/messages/offer/${acceptedOfferId}`);
        if (cancelled) return;
        const offer = res.data?.offer || res.data;
        if (offer && offer.offer_status === 'accepted' && offer.sender_id === user.user_id) {
          setAcceptedOffer(offer);
          setShowBuyNowDialog(true);
        } else if (offer && offer.offer_status === 'paid') {
          toast.info('This offer has already been paid.');
        } else if (offer && offer.offer_status !== 'accepted') {
          toast.error('This offer is not currently payable.');
        }
      } catch (e) {
        console.error('Could not load offer', e);
      }
    };
    loadOffer();
    return () => { cancelled = true; };
  }, [acceptedOfferId, user?.user_id]);
  
  const fetchAvailableInspectors = async (id) => {
    setLoadingInspectors(true);
    try {
      const res = await api.get(`/listings/${id}/inspection-options`);
      setAvailableInspectors(res.data.inspectors?.slice(0, 3) || []);
    } catch (error) {
      console.log('No inspectors available');
    } finally {
      setLoadingInspectors(false);
    }
  };

  const handleBid = async () => {
    if (!user) {
      toast.error("Please login or create an account to place a bid");
      // Trigger header auth dialog
      document.querySelector('[data-testid="login-btn"]')?.click() || 
      document.querySelector('button:has-text("Sign In")')?.click();
      return;
    }
    
    const amount = parseFloat(bidAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid bid amount");
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/listings/${listingId}/bid`, { amount });
      toast.success("Bid placed successfully!");
      // Refresh listing
      const res = await api.get(`/listings/${listingId}`);
      setListing(res.data.listing);
      setBids(res.data.bids);
      setBidAmount("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to place bid");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleWatchlist = async () => {
    if (!user) {
      document.querySelector('[data-testid="login-btn"]')?.click();
      return;
    }

    try {
      if (inWatchlist) {
        await api.delete(`/watchlist/${listingId}`);
        toast.success("Removed from watchlist");
      } else {
        const res = await api.post(`/watchlist/${listingId}`);
        toast.success("Added to watchlist");
        // Bundle discount suggestion: 2+ liked items from same seller
        const suggestion = res.data?.bundle_suggestion;
        if (suggestion && suggestion.item_count >= 2) {
          setTimeout(() => {
            toast(`You've liked ${suggestion.item_count} items from ${suggestion.seller_name}!`, {
              description: "Bundle them and save with a discount.",
              duration: 8000,
              action: {
                label: "View seller",
                onClick: () => navigate(`/profile/${suggestion.seller_id}`)
              }
            });
          }, 600);
        }
      }
      setInWatchlist(!inWatchlist);
    } catch (error) {
      toast.error("Failed to update watchlist");
    }
  };

  const handleSendMessage = async () => {
    if (!user) {
      document.querySelector('[data-testid="login-btn"]')?.click();
      return;
    }
    if (!messageText.trim()) {
      toast.error("Please enter a message");
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/messages', {
        recipient_id: listing.seller_id,
        listing_id: listingId,
        content: messageText,
        message_type: "message"
      });
      toast.success("Message sent to seller!");
      setShowMessageDialog(false);
      setMessageText("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send message");
    } finally {
      setSubmitting(false);
    }
  };

  const handleMakeOffer = async () => {
    if (!user) {
      document.querySelector('[data-testid="login-btn"]')?.click();
      return;
    }
    const amount = parseFloat(offerAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid offer amount");
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/messages/offer', {
        listing_id: listingId,
        amount: amount,
        message: offerMessage || null
      });
      toast.success("Offer sent to seller!");
      setShowOfferDialog(false);
      setOfferAmount("");
      setOfferMessage("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send offer");
    } finally {
      setSubmitting(false);
    }
  };

  const isOwnListing = user && listing?.seller_id === user.user_id;

  const handleBuyNow = () => {
    if (!user) {
      toast.error("Please login or create an account to buy this item");
      // Trigger header auth dialog
      document.querySelector('[data-testid="login-btn"]')?.click() || 
      document.querySelector('button:has-text("Sign In")')?.click();
      return;
    }
    if (isOwnListing) {
      toast.error("You cannot buy your own item");
      return;
    }
    setShowBuyNowDialog(true);
  };

  const [paymentMethod, setPaymentMethod] = useState("card");
  const [processingPayment, setProcessingPayment] = useState(false);
  const [buyerState, setBuyerState] = useState(user?.state || user?.location?.state || "");
  const [buyerCity, setBuyerCity] = useState(user?.city || user?.location?.city || "");
  // Buyer-selected shipping tier ('local' | 'national' | 'international' | 'pickup')
  const [selectedShippingTier, setSelectedShippingTier] = useState(null);
  // Full shipping address (collected before payment so seller can ship)
  const [shipName, setShipName] = useState(user?.name || "");
  const [shipStreet, setShipStreet] = useState(user?.address?.street || "");
  const [shipApt, setShipApt] = useState("");
  const [shipZip, setShipZip] = useState(user?.address?.zip_code || "");
  const [shipPhone, setShipPhone] = useState(user?.phone || "");

  const buildShipPayload = () => ({
    shipping_name: shipName.trim(),
    shipping_street: shipStreet.trim(),
    shipping_apt: shipApt.trim(),
    shipping_city: buyerCity.trim(),
    shipping_state: buyerState.trim().toUpperCase(),
    shipping_zip: shipZip.trim(),
    shipping_country: user?.country || "United States",
    shipping_phone: shipPhone.trim(),
    shipping_tier: selectedShippingTier || null
  });

  const validateShipping = () => {
    if (!shipName.trim()) return "Please enter the recipient name.";
    if (!shipStreet.trim()) return "Please enter your street address.";
    if (!buyerCity.trim()) return "Please enter your city.";
    if (!buyerState.trim()) return "Please enter your state (2-letter, e.g. CA).";
    if (!shipZip.trim()) return "Please enter your ZIP code.";
    if (!shipPhone.trim()) return "Please enter a phone number for delivery.";
    return null;
  };

  const handleStripePayment = async () => {
    const err = validateShipping();
    if (err) { toast.error(err); return; }
    setProcessingPayment(true);
    try {
      const res = await api.post('/payments/stripe/create-session', {
        listing_id: listingId,
        origin_url: window.location.origin,
        buyer_state: buyerState,
        buyer_city: buyerCity,
        buyer_country: user?.country || "United States",
        variant_id: getActiveVariant()?.variant_id || null,
        quantity: selectedQuantity,
        offer_message_id: acceptedOffer?.message_id || null,
        ...buildShipPayload()
      });
      window.location.href = res.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initiate payment");
      setProcessingPayment(false);
    }
  };

  const handlePayPalCreateOrder = async () => {
    const err = validateShipping();
    if (err) { toast.error(err); throw new Error(err); }
    try {
      const res = await api.post('/payments/paypal/create-order', {
        listing_id: listingId,
        buyer_state: buyerState,
        buyer_city: buyerCity,
        buyer_country: user?.country || "United States",
        variant_id: getActiveVariant()?.variant_id || null,
        quantity: selectedQuantity,
        offer_message_id: acceptedOffer?.message_id || null,
        ...buildShipPayload()
      });
      return res.data.order_id;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create PayPal order");
      throw error;
    }
  };

  const handlePayPalApprove = async (data) => {
    setProcessingPayment(true);
    try {
      const res = await api.post(`/payments/paypal/capture-order/${data.orderID}`);
      if (res.data.status === "COMPLETED") {
        toast.success("Payment successful! The seller will be notified.");
        setShowBuyNowDialog(false);
        // Refresh listing status
        const listingRes = await api.get(`/listings/${listingId}`);
        setListing(listingRes.data.listing);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Payment failed");
    } finally {
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-52 pb-16 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!listing) return null;

  const isAuction = listing.listing_type === "auction";
  const currentPrice = isAuction ? (listing.current_bid || listing.price || 0) : (listing.price || 0);
  const minBid = currentPrice + 1;
  const listingCurrency = listing.currency || 'USD';
  const displayPrice = formatPrice(currentPrice, listingCurrency, userCurrency);

  return (
    <div className="min-h-screen pt-52 pb-16" data-testid="item-detail-page">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Images */}
          <div>
            <div className="aspect-square rounded-2xl overflow-hidden bg-slate-100 relative group">
              <img 
                src={listing.images?.[selectedImageIndex] || listing.images?.[0] || NO_IMAGE_PLACEHOLDER} 
                alt={listing.title}
                className="w-full h-full object-cover"
              />
              {/* BidZuu Logo Watermark */}
              <div className="absolute top-4 left-4 z-10" style={{ pointerEvents: 'none' }}>
                <img 
                  src={BIDZUU_LOGO} 
                  alt="" 
                  style={{ 
                    height: '48px', 
                    width: 'auto',
                    filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.4))',
                    opacity: 0.95
                  }}
                />
              </div>
              {/* Navigation arrows */}
              {listing.images?.length > 1 && (
                <>
                  <button
                    onClick={() => setSelectedImageIndex(prev => prev === 0 ? listing.images.length - 1 : prev - 1)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  <button
                    onClick={() => setSelectedImageIndex(prev => prev === listing.images.length - 1 ? 0 : prev + 1)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                  {/* Image counter */}
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white px-3 py-1 rounded-full text-sm">
                    {selectedImageIndex + 1} / {listing.images.length}
                  </div>
                </>
              )}
            </div>
            {listing.images?.length > 1 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-4">
                {listing.images.map((img, i) => (
                  <div 
                    key={i} 
                    className={`aspect-square rounded-lg overflow-hidden bg-slate-100 cursor-pointer transition-all relative ${
                      selectedImageIndex === i 
                        ? 'ring-2 ring-emerald-500 ring-offset-2' 
                        : 'hover:opacity-80'
                    }`}
                    onClick={() => setSelectedImageIndex(i)}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                    {/* Small BidZuu logo on thumbnails */}
                    <div className="absolute top-1 left-1" style={{ pointerEvents: 'none' }}>
                      <img 
                        src={BIDZUU_LOGO} 
                        alt="" 
                        style={{ 
                          height: '14px', 
                          width: 'auto',
                          filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))',
                          opacity: 0.85
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Video Section */}
            {listing.video && (
              <div className="mt-4">
                <h3 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>
                  Product Video
                </h3>
                <div className="rounded-lg overflow-hidden border border-slate-200 bg-black">
                  <video 
                    src={listing.video.startsWith('/api') ? `${window.location.origin}${listing.video}` : listing.video}
                    controls 
                    className="w-full max-h-80 object-contain"
                    preload="metadata"
                  >
                    Your browser does not support video playback.
                  </video>
                </div>
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="secondary">{listing.category}</Badge>
                  {listing.is_sponsored && (
                    <SponsoredBadge badge={listing.sponsored_badge} />
                  )}
                  {isOutOfStock() && (
                    <Badge className="bg-red-500 text-white" data-testid="out-of-stock-badge">Out of Stock</Badge>
                  )}
                </div>
                <h1 className="font-outfit text-3xl font-bold">{listing.title}</h1>
              </div>
              <div className="flex gap-2">
                {/* Boost Button - Only visible to owner of non-sponsored listings */}
                {isOwnListing && listing.status === 'active' && !listing.is_sponsored && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white border-0 hover:from-emerald-600 hover:to-teal-600"
                    onClick={() => setShowBoostModal(true)}
                    data-testid="boost-listing-btn"
                  >
                    <Rocket className="h-4 w-4 mr-1" />
                    Boost
                  </Button>
                )}
                {/* Edit Button - Only visible to owner */}
                {isOwnListing && listing.status === 'active' && (
                  <Button 
                    variant="outline" 
                    size="icon" 
                    className="rounded-full"
                    onClick={() => navigate(`/item/${listingId}/edit`)}
                    data-testid="edit-listing-btn"
                  >
                    <Edit className="h-5 w-5" />
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="rounded-full"
                  onClick={toggleWatchlist}
                  data-testid="watchlist-toggle"
                >
                  <Heart className={`h-5 w-5 ${inWatchlist ? 'fill-red-500 text-red-500' : ''}`} />
                </Button>
                {/* Share Button */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="icon" 
                      className="rounded-full"
                      data-testid="share-btn"
                    >
                      <Share2 className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => handleShare('facebook')} className="cursor-pointer gap-3">
                      <svg className="h-4 w-4 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                      Facebook
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleShare('twitter')} className="cursor-pointer gap-3">
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                      X (Twitter)
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleShare('whatsapp')} className="cursor-pointer gap-3">
                      <svg className="h-4 w-4 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      WhatsApp
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleShare('copy')} className="cursor-pointer gap-3">
                      {linkCopied ? (
                        <Check className="h-4 w-4 text-emerald-500" />
                      ) : (
                        <Link2 className="h-4 w-4" />
                      )}
                      {linkCopied ? 'Copied!' : 'Copy Link'}
                    </DropdownMenuItem>
                    {typeof navigator !== 'undefined' && navigator.share && (
                      <DropdownMenuItem onClick={() => handleShare('native')} className="cursor-pointer gap-3">
                        <Share2 className="h-4 w-4" />
                        More Options...
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
                
                {/* Report Button */}
                {user && listing.seller_id !== user?.user_id && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowReportDialog(true)}
                    className="text-slate-500 hover:text-red-500"
                    title="Report listing"
                  >
                    <Flag className="h-5 w-5" />
                  </Button>
                )}
              </div>
            </div>

            {/* Safety Warning Banner */}
            <div 
              className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 cursor-pointer hover:bg-amber-100 transition-colors"
              onClick={() => setShowSafetyTips(true)}
            >
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-amber-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-800">Shop Safely on BidZuu</p>
                  <p className="text-xs text-amber-600">Click for safety tips • Never pay outside BidZuu</p>
                </div>
                <AlertTriangle className="h-4 w-4 text-amber-500" />
              </div>
            </div>

            <div className="flex items-center gap-3 mb-4">
              <Avatar className="h-10 w-10">
                <AvatarFallback className="bg-emerald-100 text-emerald-700">
                  {listing.seller_name?.charAt(0)?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium">{listing.seller_name}</p>
                  <TrustBadge user={{ kyc_status: listing.seller_kyc_status, email_verified: listing.seller_email_verified }} size="sm" />
                </div>
                <p className="text-sm text-muted-foreground">Seller</p>
              </div>
            </div>

            {/* Seller Trust Card */}
            {listing.seller_id && (
              <SellerTrustCard sellerId={listing.seller_id} className="mb-6" />
            )}

            {/* Unverified Seller Warning */}
            {!listing.seller_verified && !isOwnListing && (
              <div className="mb-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-800">Unverified Seller</p>
                    <p className="text-xs text-amber-600">This seller has not completed identity verification. Consider hiring an inspector.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Hire Inspector Section - For buyers on listings $500+ */}
            {!isOwnListing && listing.status === 'active' && (listing.price >= 500 || listing.current_bid >= 500) && (
              <Card className="mb-6 overflow-hidden border-emerald-200">
                <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-4 text-white">
                  <div className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    <h3 className="font-bold">Hire a Professional Inspector</h3>
                  </div>
                  <p className="text-sm text-emerald-100 mt-1">
                    Get this item verified by a certified inspector before buying
                  </p>
                </div>
                <div className="p-4">
                  {loadingInspectors ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-emerald-500" />
                    </div>
                  ) : availableInspectors.length > 0 ? (
                    <div className="space-y-3">
                      <p className="text-sm text-slate-600">Available inspectors for this item:</p>
                      {availableInspectors.map((inspector) => (
                        <div 
                          key={inspector.inspector_id}
                          className="flex items-center justify-between p-2 bg-slate-50 rounded-lg"
                        >
                          <div className="flex items-center gap-2">
                            <Avatar className="h-8 w-8">
                              <AvatarFallback className="bg-emerald-100 text-emerald-700 text-xs">
                                {inspector.name?.charAt(0)?.toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="text-sm font-medium">{inspector.name}</p>
                              <p className="text-xs text-slate-500">
                                {inspector.rating > 0 ? `⭐ ${inspector.rating.toFixed(1)}` : 'New'} • 
                                {inspector.hourly_rate && ` $${inspector.hourly_rate}/hr`}
                                {inspector.flat_fee && ` • $${inspector.flat_fee} flat`}
                              </p>
                            </div>
                          </div>
                          {inspector.verification_status === 'verified' && (
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs">Verified</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-2">
                      Browse all inspectors to find one for this item
                    </p>
                  )}
                  
                  <div className="mt-4 flex gap-2">
                    <Button 
                      onClick={() => user ? setShowInspectionModal(true) : toast.error('Please sign in to hire an inspector')}
                      className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                      data-testid="request-inspection-btn"
                    >
                      <Shield className="h-4 w-4 mr-2" />
                      Hire Inspector
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => navigate('/inspectors')}
                      className="border-emerald-200 text-emerald-700"
                    >
                      Browse All
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {/* Listing Verification Card - For sellers to verify their listings */}
            {isOwnListing && listing.status === 'active' && (
              <div className="mb-6">
                <ListingVerificationCard 
                  listingId={listing.listing_id}
                  currentStatus={listing.verification_status}
                  isOwner={isOwnListing}
                />
              </div>
            )}

            {/* Show Verification Badge if verified */}
            {listing.verification_status === 'verified' && (
              <div className="mb-4">
                <VerificationBadge status="verified" size="md" />
              </div>
            )}

            {/* View Original Listing Button - for imported items */}
            {listing.imported_from?.url && (
              <a
                href={listing.imported_from.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full mb-6 px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl font-medium transition-all shadow-md hover:shadow-lg"
                data-testid="view-original-listing-btn"
              >
                <ExternalLink className="h-4 w-4" />
                View Original Listing on {listing.imported_from.source || listing.imported_from.platform || 'Source'}
              </a>
            )}

            <Card className="p-6 mb-6 bg-slate-50">
              {/* Countdown Timer for Auctions */}
              {isAuction && listing.end_date && (
                <div className="mb-4 p-4 bg-white rounded-xl border">
                  <p className="text-sm text-muted-foreground mb-2 text-center">Time Remaining</p>
                  <CountdownTimer endDate={listing.end_date} size="large" />
                </div>
              )}
              
              <div className="flex items-end justify-between mb-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">
                    {isAuction ? (listing.current_bid ? "Current Bid" : "Starting Bid") : "Price"}
                    {userCurrency && userCurrency.code !== 'USD' && <span className="ml-1 text-xs">({userCurrency.code})</span>}
                  </p>
                  <p className="text-4xl font-bold text-emerald-600 font-outfit">
                    {displayPrice}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {isAuction && (
                    <Badge variant="secondary" className="text-sm">
                      {listing.bid_count} bid{listing.bid_count !== 1 ? 's' : ''}
                    </Badge>
                  )}
                  {isAuction && listing.reserve_price && (
                    <Badge className={listing.reserve_met ? 'bg-emerald-500' : 'bg-amber-500'}>
                      {listing.reserve_met ? 'Reserve Met' : 'Reserve Not Met'}
                    </Badge>
                  )}
                </div>
              </div>

              {isAuction ? (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input 
                        type="number"
                        placeholder={`${minBid.toFixed(2)} or more`}
                        value={bidAmount}
                        onChange={(e) => setBidAmount(e.target.value)}
                        className="pl-7"
                        min={minBid}
                        step="0.01"
                        data-testid="bid-input"
                      />
                    </div>
                    <Button 
                      className="rounded-full px-8 bg-emerald-500 hover:bg-emerald-600"
                      onClick={handleBid}
                      disabled={submitting}
                      data-testid="place-bid-btn"
                    >
                      {submitting ? "Placing..." : !user ? "Login to Bid" : "Place Bid"}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground text-center">
                    Enter ${minBid.toFixed(2)} or more
                  </p>
                </div>
              ) : (
                /* Poshmark-style Purchase Options */
                <div className="space-y-3">
                  {/* Variant Selector (Color + Size) */}
                  {listing.variants && listing.variants.length > 0 && (() => {
                    const colors = Array.from(new Set(listing.variants.map(v => v.color).filter(Boolean)));
                    const sizes = Array.from(new Set(
                      listing.variants
                        .filter(v => !selectedColor || v.color === selectedColor)
                        .map(v => v.size)
                        .filter(Boolean)
                    ));
                    const matchedVariant = listing.variants.find(v =>
                      (!colors.length || v.color === selectedColor) &&
                      (!sizes.length || v.size === selectedSize)
                    );
                    const variantPrice = matchedVariant?.price ?? listing.price;
                    const variantStock = matchedVariant?.quantity ?? null;
                    return (
                      <div className="space-y-3 p-3 bg-slate-50 rounded-lg" data-testid="variant-selector">
                        {colors.length > 0 && (
                          <div>
                            <Label className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-2 block">Color</Label>
                            <div className="flex flex-wrap gap-2">
                              {colors.map(c => {
                                const isActive = selectedColor === c;
                                return (
                                  <button
                                    key={c}
                                    type="button"
                                    onClick={() => { setSelectedColor(c); setSelectedSize(null); }}
                                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${isActive ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-slate-700 border-slate-300 hover:border-emerald-500'}`}
                                    data-testid={`variant-color-${c}`}
                                  >
                                    {c}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {sizes.length > 0 && (
                          <div>
                            <Label className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-2 block">Size</Label>
                            <div className="flex flex-wrap gap-2">
                              {sizes.map(s => {
                                const isActive = selectedSize === s;
                                return (
                                  <button
                                    key={s}
                                    type="button"
                                    onClick={() => setSelectedSize(s)}
                                    className={`px-3 py-1.5 rounded-md text-sm border transition-colors min-w-[44px] ${isActive ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-slate-700 border-slate-300 hover:border-emerald-500'}`}
                                    data-testid={`variant-size-${s}`}
                                  >
                                    {s}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {matchedVariant && (
                          <div className="flex items-center justify-between text-xs pt-1">
                            <span className="text-slate-600">
                              {matchedVariant.price !== null && matchedVariant.price !== undefined ? (
                                <>Price: <strong className="text-emerald-600">{formatPrice(variantPrice, userCurrency)}</strong></>
                              ) : null}
                            </span>
                            <span className={variantStock === 0 ? 'text-red-500 font-medium' : 'text-slate-500'} data-testid="variant-stock">
                              {variantStock === 0 ? 'Out of stock' : variantStock !== null ? `${variantStock} in stock` : ''}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Quantity Selector */}
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm font-medium">Quantity</span>
                    <div className="flex items-center gap-3">
                      <Button 
                        variant="outline" 
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setSelectedQuantity(Math.max(1, selectedQuantity - 1))}
                        disabled={selectedQuantity <= 1}
                        data-testid="qty-decrement-btn"
                      >
                        <span className="text-lg">-</span>
                      </Button>
                      <span className="w-8 text-center font-semibold" data-testid="qty-value">{selectedQuantity}</span>
                      <Button 
                        variant="outline" 
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setSelectedQuantity(selectedQuantity + 1)}
                        data-testid="qty-increment-btn"
                      >
                        <span className="text-lg">+</span>
                      </Button>
                    </div>
                  </div>

                  {/* Buy Now Button */}
                  <Button 
                    className="w-full rounded-full bg-red-500 hover:bg-red-600 text-white font-semibold h-12"
                    size="lg"
                    onClick={() => {
                      if (requiresVariantSelection()) {
                        toast.error('Please select a variant first');
                        return;
                      }
                      const v = getActiveVariant();
                      if (v && v.quantity === 0) {
                        toast.error('This variant is out of stock');
                        return;
                      }
                      if (isOutOfStock()) {
                        toast.error('This item is out of stock');
                        return;
                      }
                      handleBuyNow();
                    }}
                    disabled={listing.status !== 'active' || isOwnListing || !user || isOutOfStock()}
                    data-testid="buy-now-btn"
                  >
                    {(() => {
                      if (listing.status === 'sold') return 'Sold';
                      if (isOwnListing) return 'Your Listing';
                      if (!user) return 'Login to Buy';
                      if (isOutOfStock()) return 'Out of Stock';
                      const v = getActiveVariant();
                      const unitPrice = (v && v.price !== null && v.price !== undefined) ? v.price : listing.price;
                      return `Buy Now • ${formatPrice(unitPrice * selectedQuantity, userCurrency)}`;
                    })()}
                  </Button>

                  {/* Add to Cart Button */}
                  {listing.status === 'active' && !isOwnListing && user && !isOutOfStock() && (
                    <Button 
                      variant="outline"
                      className="w-full rounded-full border-2 border-slate-800 text-slate-800 hover:bg-slate-100 font-semibold h-12"
                      size="lg"
                      onClick={async () => {
                        if (requiresVariantSelection()) {
                          toast.error('Please select a variant first');
                          return;
                        }
                        const v = getActiveVariant();
                        if (v && v.quantity === 0) {
                          toast.error('This variant is out of stock');
                          return;
                        }
                        setAddingToCart(true);
                        try {
                          await api.post('/cart/add', {
                            listing_id: listing.listing_id,
                            quantity: selectedQuantity,
                            variant_id: v?.variant_id || null
                          });
                          toast.success('Added to cart!');
                          window.dispatchEvent(new Event('cart:updated'));
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Failed to add to cart');
                        } finally {
                          setAddingToCart(false);
                        }
                      }}
                      disabled={addingToCart}
                      data-testid="add-to-cart-btn"
                    >
                      {addingToCart ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <ShoppingCart className="h-4 w-4 mr-2" />
                      )}
                      Add to Cart
                    </Button>
                  )}

                  {/* Make Offer Button */}
                  {listing.status === 'active' && !isOwnListing && user && (
                    <Button 
                      variant="outline"
                      className="w-full rounded-full border-2 border-emerald-500 text-emerald-600 hover:bg-emerald-50 font-semibold h-12"
                      size="lg"
                      onClick={() => setShowOfferDialog(true)}
                      data-testid="make-offer-btn"
                    >
                      <Tag className="h-4 w-4 mr-2" />
                      Make an Offer
                    </Button>
                  )}

                  {/* Add to Bundle / Wishlist */}
                  <div className="flex gap-2">
                    <Button 
                      variant="ghost"
                      className={`flex-1 rounded-full ${inWatchlist ? 'text-red-500' : 'text-slate-600'}`}
                      onClick={toggleWatchlist}
                    >
                      <Heart className={`h-4 w-4 mr-2 ${inWatchlist ? 'fill-current' : ''}`} />
                      {inWatchlist ? 'Saved' : 'Save'}
                    </Button>
                    <Button 
                      variant="ghost"
                      className="flex-1 rounded-full text-slate-600"
                      onClick={handleShare}
                    >
                      <Share2 className="h-4 w-4 mr-2" />
                      Share
                    </Button>
                  </div>
                </div>
              )}
            </Card>

            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Description</h3>
                <p className="text-muted-foreground">{listing.description}</p>
              </div>
              <div className="flex gap-4 flex-wrap">
                <div>
                  <p className="text-sm text-muted-foreground">Condition</p>
                  <p className="font-medium capitalize">{listing.condition}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Type</p>
                  <p className="font-medium capitalize">{listing.listing_type}</p>
                </div>
                {listing.quantity > 1 && (
                  <div>
                    <p className="text-sm text-muted-foreground">Available</p>
                    <p className="font-medium">{listing.quantity} items</p>
                  </div>
                )}
                {listing.size && listing.size !== 'n_a' && (
                  <div>
                    <p className="text-sm text-muted-foreground">Size</p>
                    <p className="font-medium uppercase">{listing.size}</p>
                  </div>
                )}
                {listing.brand && (
                  <div>
                    <p className="text-sm text-muted-foreground">Brand</p>
                    <p className="font-medium">{listing.brand}</p>
                  </div>
                )}
                {listing.colors && listing.colors.length > 0 && (
                  <div>
                    <p className="text-sm text-muted-foreground">Color</p>
                    <p className="font-medium capitalize">{listing.colors.join(", ")}</p>
                  </div>
                )}
              </div>
              
              {/* Location Display */}
              {listing.location && (listing.location.city || listing.location.country) && (
                <div className="p-4 bg-slate-50 rounded-xl border">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-emerald-600" />
                    <div>
                      <p className="font-medium">
                        {[listing.location.city, listing.location.country].filter(Boolean).join(", ")}
                      </p>
                      <p className="text-xs text-muted-foreground">Item Location</p>
                      {/* Show map status */}
                      {listing.location.latitude && listing.location.longitude ? (
                        <p className="text-xs text-emerald-600 mt-1">✓ Visible on map</p>
                      ) : (
                        <p className="text-xs text-orange-500 mt-1">⚠ Missing coordinates - edit listing to fix</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Shipping Info — buyer-selectable tiers (eBay/Mercari-style) */}
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Package className="h-5 w-5 text-blue-600" />
                  Shipping
                </h3>
                {(() => {
                  const s = listing.shipping || {};
                  if (s.free_shipping) {
                    return (
                      <div className="flex items-center gap-2 text-emerald-600">
                        <span className="text-lg">✓</span>
                        <span className="font-medium">Free Shipping</span>
                      </div>
                    );
                  }
                  // Build tier options from whatever seller configured
                  const tiers = [];
                  if (s.local_rate !== null && s.local_rate !== undefined && s.local_rate !== "") {
                    tiers.push({ id: 'local', label: 'Same State', sub: '3-5 business days', rate: Number(s.local_rate) });
                  }
                  if (s.national_rate !== null && s.national_rate !== undefined && s.national_rate !== "") {
                    tiers.push({ id: 'national', label: 'Standard (US)', sub: '3-7 business days', rate: Number(s.national_rate) });
                  }
                  if (s.international_rate !== null && s.international_rate !== undefined && s.international_rate !== "") {
                    tiers.push({ id: 'international', label: 'International', sub: '7-14 business days', rate: Number(s.international_rate) });
                  }
                  if (s.local_pickup) {
                    tiers.push({ id: 'pickup', label: 'Local Pickup', sub: 'Same day', rate: 0 });
                  }
                  if (tiers.length === 0) {
                    return <p className="text-sm text-muted-foreground">Contact seller for shipping rates</p>;
                  }
                  // Auto-select cheapest if nothing chosen
                  if (selectedShippingTier === null) {
                    const cheapest = [...tiers].sort((a,b) => a.rate - b.rate)[0];
                    setTimeout(() => setSelectedShippingTier(cheapest.id), 0);
                  }
                  return (
                    <div className="space-y-2">
                      {tiers.map(t => {
                        const isSelected = selectedShippingTier === t.id;
                        return (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => setSelectedShippingTier(t.id)}
                            className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg border text-left transition ${
                              isSelected
                                ? 'border-emerald-500 bg-white shadow-sm ring-2 ring-emerald-200'
                                : 'border-slate-200 bg-white hover:border-slate-300'
                            }`}
                            data-testid={`shipping-tier-${t.id}`}
                          >
                            <div className="flex items-center gap-2">
                              <span className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${isSelected ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300'}`}>
                                {isSelected && <span className="block h-full w-full rounded-full bg-white scale-50" />}
                              </span>
                              <div>
                                <p className="text-sm font-medium text-slate-900">{t.label}</p>
                                <p className="text-xs text-muted-foreground">{t.sub}</p>
                              </div>
                            </div>
                            <span className={`text-sm font-bold ${t.rate === 0 ? 'text-emerald-600' : 'text-slate-900'}`}>
                              {t.rate === 0 ? 'Free' : `$${t.rate.toFixed(2)}`}
                            </span>
                          </button>
                        );
                      })}
                      {listing.location?.city && (
                        <p className="text-xs text-muted-foreground pt-1">
                          Ships from {listing.location.city}{listing.location.country ? `, ${listing.location.country}` : ''}
                        </p>
                      )}
                    </div>
                  );
                })()}
                
                {/* Shipping Estimate Calculator */}
                <div className="mt-4 pt-4 border-t border-blue-200">
                  <p className="text-sm font-medium mb-2 flex items-center gap-2">
                    <Truck className="h-4 w-4" />
                    Calculate Shipping
                  </p>
                  <div className="flex gap-2">
                    <Input 
                      placeholder="Enter ZIP code"
                      value={buyerZip}
                      onChange={(e) => setBuyerZip(e.target.value)}
                      className="h-9"
                      maxLength={10}
                    />
                    <Button 
                      size="sm" 
                      onClick={() => fetchShippingEstimate(buyerZip)}
                      disabled={!buyerZip || loadingShipping}
                      className="bg-blue-500 hover:bg-blue-600"
                    >
                      {loadingShipping ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Calculate'}
                    </Button>
                  </div>
                  
                  {shippingEstimates && (
                    <div className="mt-3 space-y-2">
                      {shippingEstimates.distance_miles && (
                        <p className="text-xs text-muted-foreground">
                          📍 {shippingEstimates.distance_miles.toFixed(0)} miles from {shippingEstimates.seller_location?.city || 'seller'}
                        </p>
                      )}
                      {shippingEstimates.estimates?.map((est, i) => (
                        <div key={i} className="flex justify-between items-center text-sm p-2 bg-white rounded">
                          <div>
                            <p className="font-medium">{est.method_label}</p>
                            <p className="text-xs text-muted-foreground">{est.delivery_time}</p>
                          </div>
                          <div className="text-right">
                            {est.is_free ? (
                              <span className="text-emerald-600 font-medium">FREE</span>
                            ) : est.cost === 0 ? (
                              <span className="text-emerald-600 font-medium">Free</span>
                            ) : (
                              <span className="font-medium">${est.cost.toFixed(2)}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Same-Day Local Delivery via Burq */}
              {listing.location?.latitude && listing.location?.longitude && listing.status === 'active' && !isOwnListing && (
                <SameDayDeliveryOption 
                  listing={listing} 
                  user={user}
                  onDeliveryCreated={(delivery) => {
                    toast.success(
                      <div>
                        <p className="font-medium">Same-day delivery requested!</p>
                        <p className="text-sm">Track your delivery in real-time.</p>
                      </div>
                    );
                    navigate(`/delivery/track/${delivery.delivery_id}`);
                  }}
                />
              )}

              {/* Local Pickup Option */}
              {listing.local_pickup_available && (
                <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-amber-600" />
                    Local Pickup Available
                  </h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    {listing.local_pickup_address || listing.location?.city || 'Contact seller for location'}
                  </p>
                  <p className="text-xs text-amber-700">
                    Pay cash when you pick up the item
                  </p>
                  {!isOwnListing && listing.status === 'active' && (
                    <Button 
                      variant="outline"
                      className="mt-3 w-full border-amber-300 text-amber-700 hover:bg-amber-100"
                      onClick={async () => {
                        if (!user) {
                          document.querySelector('[data-testid="login-btn"]')?.click();
                          return;
                        }
                        try {
                          const res = await api.post('/purchase/local-pickup', { listing_id: listingId });
                          toast.success(
                            <div>
                              <p className="font-medium">Pickup Initiated!</p>
                              <p className="text-sm">Your pickup code: <span className="font-mono font-bold">{res.data.pickup_code}</span></p>
                              <p className="text-xs mt-1">Show this code to the seller when picking up.</p>
                            </div>,
                            { duration: 15000 }
                          );
                          // Refresh listing
                          const listingRes = await api.get(`/listings/${listingId}`);
                          setListing(listingRes.data.listing);
                        } catch (error) {
                          toast.error(error.response?.data?.detail || "Failed to initiate pickup");
                        }
                      }}
                      data-testid="local-pickup-btn"
                    >
                      <MapPin className="h-4 w-4 mr-2" />
                      Arrange Local Pickup
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Bid History */}
            {isAuction && bids.length > 0 && (
              <Card className="mt-6 p-4">
                <h3 className="font-semibold mb-4">Bid History</h3>
                <div className="space-y-3 max-h-48 overflow-y-auto">
                  {bids.map((bid, i) => (
                    <div key={bid.bid_id} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="bg-slate-100 text-slate-600 text-xs">
                            {bid.bidder_name?.charAt(0)?.toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm">{bid.bidder_name}</span>
                        {i === 0 && <Badge className="bg-emerald-500 text-xs">Highest</Badge>}
                      </div>
                      <span className="font-semibold">${bid.amount.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Contact Seller Section */}
            {!isOwnListing && (
              <Card className="mt-6 p-4">
                <h3 className="font-semibold mb-4">Contact Seller</h3>
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    className="flex-1 gap-2"
                    onClick={() => user ? setShowMessageDialog(true) : document.querySelector('[data-testid="login-btn"]')?.click()}
                    data-testid="message-seller-btn"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Message Seller
                  </Button>
                  <Button 
                    className="flex-1 gap-2 bg-emerald-500 hover:bg-emerald-600"
                    onClick={() => user ? setShowOfferDialog(true) : document.querySelector('[data-testid="login-btn"]')?.click()}
                    data-testid="make-offer-btn"
                  >
                    <DollarSign className="h-4 w-4" />
                    Make Offer
                  </Button>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Message Seller Dialog */}
      <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Message Seller</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-200">
                <img src={listing?.images?.[0] || NO_IMAGE_PLACEHOLDER} alt="" className="w-full h-full object-cover" />
              </div>
              <div>
                <p className="font-medium text-sm">{listing?.title}</p>
                <p className="text-xs text-muted-foreground">To: {listing?.seller_name}</p>
              </div>
            </div>
            <div>
              <Label>Your Message</Label>
              <Textarea
                placeholder="Hi, I'm interested in this item..."
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                rows={4}
                className="mt-1"
                data-testid="message-input"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowMessageDialog(false)}>Cancel</Button>
              <Button 
                className="bg-emerald-500 hover:bg-emerald-600 gap-2"
                onClick={handleSendMessage}
                disabled={submitting}
                data-testid="send-message-btn"
              >
                <Send className="h-4 w-4" />
                {submitting ? "Sending..." : "Send Message"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Make Offer Dialog */}
      <Dialog open={showOfferDialog} onOpenChange={setShowOfferDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Make an Offer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-200">
                <img src={listing?.images?.[0] || NO_IMAGE_PLACEHOLDER} alt="" className="w-full h-full object-cover" />
              </div>
              <div>
                <p className="font-medium text-sm">{listing?.title}</p>
                <p className="text-emerald-600 font-semibold">${listing?.price?.toFixed(2)} {isAuction ? '(Starting)' : ''}</p>
              </div>
            </div>
            <div>
              <Label>Your Offer Amount *</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  type="number"
                  placeholder="Enter your offer"
                  value={offerAmount}
                  onChange={(e) => setOfferAmount(e.target.value)}
                  className="pl-7"
                  min="1"
                  step="0.01"
                  data-testid="offer-amount-input"
                />
              </div>
            </div>
            <div>
              <Label>Message (Optional)</Label>
              <Textarea
                placeholder="Add a message to support your offer..."
                value={offerMessage}
                onChange={(e) => setOfferMessage(e.target.value)}
                rows={3}
                className="mt-1"
                data-testid="offer-message-input"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowOfferDialog(false)}>Cancel</Button>
              <Button 
                className="bg-emerald-500 hover:bg-emerald-600 gap-2"
                onClick={handleMakeOffer}
                disabled={submitting}
                data-testid="submit-offer-btn"
              >
                <DollarSign className="h-4 w-4" />
                {submitting ? "Sending..." : "Send Offer"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Buy Now Confirmation Dialog */}
      <Dialog open={showBuyNowDialog} onOpenChange={setShowBuyNowDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Complete Your Purchase</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
              <div className="w-20 h-20 rounded-lg overflow-hidden bg-slate-200">
                <img src={listing?.images?.[0] || NO_IMAGE_PLACEHOLDER} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="flex-1">
                <p className="font-medium">{listing?.title}</p>
                <p className="text-sm text-muted-foreground">Sold by {listing?.seller_name}</p>
                {acceptedOffer ? (
                  <div className="mt-1">
                    <p className="text-2xl font-bold text-emerald-600">${Number(acceptedOffer.offer_amount).toFixed(2)}</p>
                    <p className="text-xs text-emerald-700 font-medium">Accepted offer · was ${listing?.price?.toFixed(2)}</p>
                  </div>
                ) : (
                  <p className="text-2xl font-bold text-emerald-600 mt-1">${listing?.price?.toFixed(2)}</p>
                )}
              </div>
            </div>

            {/* Ship-to (full address used for shipping label + tax) */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Ship To</Label>
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Full name"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  value={shipName}
                  onChange={(e) => setShipName(e.target.value)}
                  data-testid="ship-name-input"
                />
                <input
                  type="text"
                  placeholder="Street address"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  value={shipStreet}
                  onChange={(e) => setShipStreet(e.target.value)}
                  data-testid="ship-street-input"
                />
                <input
                  type="text"
                  placeholder="Apt, suite, unit (optional)"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  value={shipApt}
                  onChange={(e) => setShipApt(e.target.value)}
                  data-testid="ship-apt-input"
                />
                <div className="grid grid-cols-3 gap-2">
                  <input
                    type="text"
                    placeholder="City"
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    value={buyerCity}
                    onChange={(e) => setBuyerCity(e.target.value)}
                    data-testid="buyer-city-input"
                  />
                  <input
                    type="text"
                    placeholder="State"
                    maxLength={2}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm uppercase"
                    value={buyerState}
                    onChange={(e) => setBuyerState(e.target.value.toUpperCase())}
                    data-testid="buyer-state-input"
                  />
                  <input
                    type="text"
                    placeholder="ZIP"
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    value={shipZip}
                    onChange={(e) => setShipZip(e.target.value)}
                    data-testid="ship-zip-input"
                  />
                </div>
                <input
                  type="tel"
                  placeholder="Phone number (for delivery)"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  value={shipPhone}
                  onChange={(e) => setShipPhone(e.target.value)}
                  data-testid="ship-phone-input"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">The seller uses this to print your shipping label. Final amount confirmed on the payment page.</p>
            </div>

            {/* Order Breakdown */}
            <PurchaseBreakdown
              listing={listing}
              quantity={selectedQuantity}
              variant={getActiveVariant()}
              buyerState={buyerState}
              buyerCity={buyerCity}
              offerMessageId={acceptedOffer?.message_id || null}
              shippingTier={selectedShippingTier}
            />
            
            {/* Payment Method Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Select Payment Method</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setPaymentMethod("card")}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    paymentMethod === "card" 
                      ? "border-emerald-500 bg-emerald-50" 
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <CreditCard className={`h-6 w-6 mx-auto mb-2 ${paymentMethod === "card" ? "text-emerald-600" : "text-slate-500"}`} />
                  <p className={`text-sm font-medium ${paymentMethod === "card" ? "text-emerald-700" : "text-slate-700"}`}>
                    Credit/Debit Card
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">Visa, Mastercard, Amex</p>
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentMethod("paypal")}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    paymentMethod === "paypal" 
                      ? "border-blue-500 bg-blue-50" 
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <svg className={`h-6 w-6 mx-auto mb-2 ${paymentMethod === "paypal" ? "text-blue-600" : "text-slate-500"}`} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.771.771 0 0 1 .76-.65h6.727c2.215 0 3.912.505 4.97 1.48.987.91 1.37 2.178 1.14 3.77-.31 2.14-1.35 3.73-2.91 4.58-.99.53-2.19.79-3.53.79H9.674l-.964 7.65a.641.641 0 0 1-.634.547zm9.35-15.3c-.06.4-.14.78-.25 1.15-.98 3.39-3.86 4.29-6.44 4.29H8.08L6.98 20.2h2.69l.78-6.21h2.21c2.52 0 4.69-.94 5.39-4.07.28-1.26.15-2.31-.68-3.09-.08-.08-.16-.15-.25-.22.06.05.1.1.14.16z"/>
                  </svg>
                  <p className={`text-sm font-medium ${paymentMethod === "paypal" ? "text-blue-700" : "text-slate-700"}`}>
                    PayPal
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">Pay with PayPal account</p>
                </button>
              </div>
            </div>

            {/* Payment Buttons */}
            <div className="pt-2">
              {paymentMethod === "card" ? (
                <Button 
                  className="w-full bg-emerald-500 hover:bg-emerald-600 gap-2 h-12"
                  onClick={handleStripePayment}
                  disabled={processingPayment}
                  data-testid="pay-with-card-btn"
                >
                  {processingPayment ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-4 w-4" />
                      Pay ${(acceptedOffer ? Number(acceptedOffer.offer_amount) : listing?.price)?.toFixed(2)} with Card
                    </>
                  )}
                </Button>
              ) : (
                <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: "USD" }}>
                  <PayPalButtons
                    style={{ layout: "horizontal", color: "blue", shape: "rect", label: "pay", height: 48 }}
                    createOrder={handlePayPalCreateOrder}
                    onApprove={handlePayPalApprove}
                    onError={(err) => {
                      console.error("PayPal error:", err);
                      const msg = err?.message || err?.toString() || "Please try again.";
                      toast.error(`PayPal payment failed: ${msg.substring(0, 200)}`);
                    }}
                    disabled={processingPayment}
                  />
                </PayPalScriptProvider>
              )}
            </div>

            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground pt-2">
              <Shield className="h-3 w-3" />
              <span>Secure payment powered by {paymentMethod === "card" ? "Stripe" : "PayPal"}</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Boost Listing Modal */}
      <BoostListingModal 
        listing={listing}
        isOpen={showBoostModal}
        onClose={() => setShowBoostModal(false)}
        onSuccess={() => {
          setShowBoostModal(false);
          // Refresh listing
          api.get(`/listings/${listingId}`).then(res => setListing(res.data.listing));
        }}
      />

      {/* Report Listing Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag className="h-5 w-5 text-red-500" />
              Report Listing
            </DialogTitle>
            <DialogDescription>
              Help us keep BidZuu safe by reporting suspicious listings.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Reason for reporting *</Label>
              <Select value={reportReason} onValueChange={setReportReason}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_REASONS.map(reason => (
                    <SelectItem key={reason.value} value={reason.value}>
                      {reason.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Additional details (optional)</Label>
              <Textarea
                value={reportDetails}
                onChange={(e) => setReportDetails(e.target.value)}
                placeholder="Provide any additional information that might help us investigate..."
                className="mt-1"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReportDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleReportListing}
              disabled={submittingReport || !reportReason}
              className="bg-red-600 hover:bg-red-700"
            >
              {submittingReport ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Flag className="h-4 w-4 mr-2" />}
              Submit Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Safety Tips Dialog */}
      <Dialog open={showSafetyTips} onOpenChange={setShowSafetyTips}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-emerald-600" />
              Shop Safely on BidZuu
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-emerald-50 rounded-lg p-4">
              <h4 className="font-semibold text-emerald-800 mb-2">✅ Safe Buying Tips</h4>
              <ul className="text-sm text-emerald-700 space-y-1">
                <li>• Always pay through BidZuu's secure payment system</li>
                <li>• Use tracked shipping for all purchases</li>
                <li>• Check seller ratings and verification status</li>
                <li>• If a deal seems too good to be true, it probably is</li>
                <li>• Keep all communication within BidZuu messages</li>
              </ul>
            </div>
            
            <div className="bg-red-50 rounded-lg p-4">
              <h4 className="font-semibold text-red-800 mb-2">🚨 Warning Signs</h4>
              <ul className="text-sm text-red-700 space-y-1">
                <li>• Requests to pay via gift cards, wire transfer, or crypto</li>
                <li>• Pressure to complete transaction quickly</li>
                <li>• Requests to communicate outside BidZuu</li>
                <li>• Prices significantly below market value</li>
                <li>• Sellers who refuse to provide additional photos</li>
              </ul>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-semibold text-blue-800 mb-2">🛡️ BidZuu Protection</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• All payments are protected through our secure system</li>
                <li>• Buyer protection for items not as described</li>
                <li>• 24/7 support for disputes and issues</li>
                <li>• Report suspicious activity anytime</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowSafetyTips(false)} className="w-full bg-emerald-600 hover:bg-emerald-700">
              Got it, thanks!
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Inspection Modal */}
      <RequestInspectionModal
        isOpen={showInspectionModal}
        onClose={() => setShowInspectionModal(false)}
        listing={listing}
        onSuccess={() => {
          setShowInspectionModal(false);
          toast.success('Inspection requested!');
        }}
      />
    </div>
  );
};

export default ItemDetailPage;
